import merge from 'lodash/merge';
import { useEffect, useRef, useState } from 'react';
import { FieldTypes } from '../../constants';
import {
  useFormSubmitterContext,
  type FormSubmitterDispatch,
} from '../form-submitter/form-submitter-context';
import { logicEngine } from './common/logic-engine';
import { Operators, type OperatorType } from './constants';
import type { FormField, LogicItem } from './logic-builder-types';

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const escapeRegex = (str: any) =>
  String(str).replace(/[.*+?^${}()|[\]\\"]/g, '\\$&');
/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const escapeJsonataString = (value: any) => JSON.stringify(String(value));

export const OperatorToExpression: Partial<
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  Record<OperatorType, (fieldId: string, value?: any) => string>
> = {
  [Operators.EQUALS]: (fieldId, value) =>
    ['boolean', 'number'].includes(typeof value)
      ? `${fieldId} = ${value}`
      : `${fieldId} = ${escapeJsonataString(value)}`,
  [Operators.DOES_NOT_EQUAL]: (fieldId, value) =>
    ['boolean', 'number'].includes(typeof value)
      ? `${fieldId} != ${value}`
      : `${fieldId} != ${escapeJsonataString(value)}`,
  [Operators.GREATER_THAN]: (fieldId, value) => `${fieldId} > ${value}`,
  [Operators.GREATER_THAN_OR_EQUAL]: (fieldId, value) =>
    `${fieldId} >= ${value}`,
  [Operators.LESS_THAN]: (fieldId, value) => `${fieldId} < ${value}`,
  [Operators.LESS_THAN_OR_EQUAL]: (fieldId, value) => `${fieldId} <= ${value}`,
  [Operators.CONTAINS]: (fieldId, value) =>
    `$contains(${fieldId}, /${escapeRegex(value)}/)`,
  [Operators.DOES_NOT_CONTAIN]: (fieldId, value) =>
    `$not($contains(${fieldId}, /${escapeRegex(value)}/))`,
  [Operators.BEGINS_WITH]: (fieldId, value) =>
    `$contains(${fieldId}, /^${escapeRegex(value)}/)`,
  [Operators.ENDS_WITH]: (fieldId, value) =>
    `$contains(${fieldId}, /${escapeRegex(value)}$/)`,
  [Operators.IS_EMPTY]: fieldId =>
    `$not($exists(${fieldId})) or ${fieldId} = ""`,
  [Operators.IS_NOT_EMPTY]: fieldId => `${fieldId} != ""`,
  // special case for INCLUDES single select METADATA fields. Basically they need the same descriptions as the multi, but
  // the data is structured differently and needs different comparators.
  [Operators.INCLUDES]: (fieldId, value) =>
    `${fieldId} = ${escapeJsonataString(value)}`,
  [Operators.DOES_NOT_INCLUDE]: (fieldId, value) =>
    `${fieldId} != ${escapeJsonataString(value)}`,
};

export const OperatorToListExpression: Partial<
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  Record<OperatorType, (fieldId: string, value?: any) => string>
> = {
  [Operators.EQUALS]: (fieldId, value) =>
    `${escapeJsonataString(value)} in ${fieldId}`,
  [Operators.DOES_NOT_EQUAL]: (fieldId, value) =>
    `$not(${escapeJsonataString(value)} in ${fieldId})`,
  [Operators.IS_EMPTY]: fieldId => `$count(${fieldId}) <= 0`,
  [Operators.IS_NOT_EMPTY]: fieldId => `$count(${fieldId}) > 0`,
  [Operators.INCLUDES]: (fieldId, value) =>
    `${escapeJsonataString(value)} in ${fieldId}`,
  [Operators.DOES_NOT_INCLUDE]: (fieldId, value) =>
    `$not(${escapeJsonataString(value)} in ${fieldId})`,
};
export const OperatorToDateFunction: Partial<
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  Record<OperatorType, (fieldId: string, value?: any) => any>
> = {
  [Operators.EQUALS]: (fieldId, value) => {
    const targetDate = new Date(value);
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    return (userVals: any) => {
      // the field ID and value are memoized
      const val = userVals[fieldId];
      if (!val) return false;
      // now parse the dates;
      const inputDate = new Date(val);
      return (
        targetDate.getUTCDate() === inputDate.getUTCDate() &&
        targetDate.getUTCMonth() === inputDate.getUTCMonth() &&
        targetDate.getUTCFullYear() === inputDate.getUTCFullYear()
      );
    };
  },
  [Operators.DOES_NOT_EQUAL]: (fieldId, value) => {
    const targetDate = new Date(value);
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    return (userVals: any) => {
      // the field ID and value are memoized
      const val = userVals[fieldId];
      if (!val) return false;
      // now parse the dates;
      const inputDate = new Date(val);
      return !(
        targetDate.getUTCDate() === inputDate.getUTCDate() &&
        targetDate.getUTCMonth() === inputDate.getUTCMonth() &&
        targetDate.getUTCFullYear() === inputDate.getUTCFullYear()
      );
    };
  },
  [Operators.IS_EMPTY]: fieldId => {
    // attempting to use JSONata where we can.  Fewer deltas in the logic the easier to maintain
    const escaped = `$.\`${fieldId}\``;
    return `${escaped} = ""`;
  },
  [Operators.IS_NOT_EMPTY]: fieldId => {
    // attempting to use JSONata where we can.  Fewer deltas in the logic the easier to maintain
    const escaped = `$.\`${fieldId}\``;
    return `${escaped} != ""`;
  },
  [Operators.GREATER_THAN]: (fieldId, value) => {
    const targetDate = new Date(value);
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    return (userVals: any) => {
      // the field ID and value are memoized
      const val = userVals[fieldId];
      if (!val) return false;
      // now parse the dates;
      const inputDate = new Date(val);
      // we don't want to look at the timestamps, so look at each date component.
      return (
        targetDate.getUTCDate() < inputDate.getUTCDate() &&
        targetDate.getUTCMonth() <= inputDate.getUTCMonth() && // remember that tomorrow is still greater than today, but the same month ;-)
        targetDate.getUTCFullYear() <= inputDate.getUTCFullYear()
      );
    };
  },
  [Operators.GREATER_THAN_OR_EQUAL]: (fieldId, value) => {
    const targetDate = new Date(value);
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    return (userVals: any) => {
      // the field ID and value are memoized
      const val = userVals[fieldId];
      if (!val) return false;
      // now parse the dates;
      const inputDate = new Date(val);
      // we don't want to look at the timestamps, so look at each date component.
      return (
        targetDate.getUTCDate() <= inputDate.getUTCDate() &&
        targetDate.getUTCMonth() <= inputDate.getUTCMonth() &&
        targetDate.getUTCFullYear() <= inputDate.getUTCFullYear()
      );
    };
  },
  [Operators.LESS_THAN]: (fieldId, value) => {
    const targetDate = new Date(value);
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    return (userVals: any) => {
      // the field ID and value are memoized
      const val = userVals[fieldId];
      if (!val) return false;
      // now parse the dates;
      const inputDate = new Date(val);
      // we don't want to look at the timestamps, so look at each date component.
      return (
        targetDate.getUTCDate() > inputDate.getUTCDate() &&
        targetDate.getUTCMonth() >= inputDate.getUTCMonth() && // remember that yesterday is still less than today, but the same month ;-)
        targetDate.getUTCFullYear() >= inputDate.getUTCFullYear()
      );
    };
  },
  [Operators.LESS_THAN_OR_EQUAL]: (fieldId, value) => {
    const targetDate = new Date(value);
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    return (userVals: any) => {
      // the field ID and value are memoized
      const val = userVals[fieldId];
      if (!val) return false;
      // now parse the dates;
      const inputDate = new Date(val);
      // we don't want to look at the timestamps, so look at each date component.
      return (
        targetDate.getUTCDate() >= inputDate.getUTCDate() &&
        targetDate.getUTCMonth() >= inputDate.getUTCMonth() &&
        targetDate.getUTCFullYear() >= inputDate.getUTCFullYear()
      );
    };
  },
};

// @ts-expect-error [MIGRATION-SUPPRESSION]: TS2749
const enumerableFieldTypes: Array<FieldTypes> = [
  FieldTypes.TYPE_CHECKBOX_FIELD,
  FieldTypes.TYPE_RADIO_FIELD,
  FieldTypes.TYPE_SELECT_FIELD,
  FieldTypes.TYPE_TAXONOMY_FIELD,
];

// So this is an example of "big red button" for a small blue knob :-P  This function produces a _thing_ that is usable
// to evaluate a user value against the logic def.  It could be a JSONata, a function, or anything!
const generateJsonataExpression = (
  fieldId: string,
  operator: OperatorType,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  value: any,
  formField?: FormField | null,
) => {
  // remember that value comes from the def in logics.  FieldID is user input.
  const escapedFieldId = `$.\`${fieldId}\``;

  if (
    formField &&
    formField.type &&
    enumerableFieldTypes.includes(formField.type)
  ) {
    // we need to check for the ONE type of select field that doesn't have a list of values.
    const singleSelect =
      formField.type === FieldTypes.TYPE_SELECT_FIELD &&
      formField.multiSelect &&
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2367
      formField.multiSelect === false;

    if (operator in OperatorToListExpression && !singleSelect) {
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2722
      return OperatorToListExpression[operator](escapedFieldId, value);
    }
  }

  if (
    formField &&
    formField.type &&
    formField.type === FieldTypes.TYPE_DATE_FIELD
  ) {
    if (operator in OperatorToDateFunction) {
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2722
      return OperatorToDateFunction[operator](fieldId, value);
    }
  }

  if (operator in OperatorToExpression) {
    // honestly here we'll have to "guess" by type to see what set of JSONata functions to use.  This is a pain in
    // ass, but JSONata doesn't allow for type checking / safety.
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2722
    return OperatorToExpression[operator](escapedFieldId, value);
  }

  throw new Error(`Unknown operator: ${operator}`);
};

// So we need the logic and the items the represent to suss out lurking lists in the data.
// JSONata is purely an expression syntax with not type awareness, so we need to know the type of the field.
const computeLogicItems = (
  logicContent: Array<LogicItem>,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  formFields: any,
): Array<LogicItem> => {
  return logicContent.map(
    (logicItem: LogicItem): LogicItem => ({
      ...logicItem,
      conditionGroups: logicItem.conditionGroups.map(conditionGroup => ({
        ...conditionGroup,
        conditions: conditionGroup.conditions.map(condition => {
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2538
          const fieldType = formFields[condition.fieldId].type;

          // Convert the condition value to a number for number fields and metadata number fields
          const conditionValue =
            fieldType === FieldTypes.TYPE_NUMBER_FIELD ||
            // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2538
            formFields[condition.fieldId].textType === 'number'
              ? Number(condition.value)
              : condition.value;
          return {
            ...condition,
            // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2538
            fieldType: formFields[condition.fieldId].type,
            // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2538
            textType: formFields[condition.fieldId].textType,
            expression: condition.fieldId
              ? generateJsonataExpression(
                  condition.fieldId,
                  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2345
                  condition.operator,
                  conditionValue,
                  formFields[condition.fieldId] || null,
                )
              : undefined,
          };
        }),
      })),
    }),
  );
};

const applyLogicEngine = async (
  preComputedLogics: Array<LogicItem>,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  response: any,
  dispatch: FormSubmitterDispatch,
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
  initialComponents,
) => {
  const stateChanges = await logicEngine(preComputedLogics, response);
  const mergedStateChanges = merge({}, initialComponents, stateChanges);

  dispatch({
    type: 'ACTION_UPDATE_COMPONENTS',
    payload: mergedStateChanges,
  });
};

const useFormLogic = () => {
  const { state, dispatch } = useFormSubmitterContext();
  const initialComponentsRef = useRef(null);
  const isMounted = useRef(true);

  // Track component mounted state
  useEffect(() => {
    isMounted.current = true;

    return function cleanup() {
      isMounted.current = false;
    };
  }, []);

  // Initialize the initialComponentsRef once the state.components is populated
  useEffect(() => {
    if (
      !initialComponentsRef.current &&
      state.components &&
      Object.keys(state.components).length > 0
    ) {
      initialComponentsRef.current = state.components;
    }
  }, [state.components]);

  const [loadedLogicContent, setLoadedLogicContent] = useState(null);
  const preComputedLogicsRef = useRef();

  // Process and cache the logic content once it's loaded
  useEffect(() => {
    if (!loadedLogicContent && state.form?.content?.logic?.length > 0) {
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
      preComputedLogicsRef.current = computeLogicItems(
        state.form.content.logic,
        state.form.content.components,
      );
      setLoadedLogicContent(state.form.content.logic);
    }
  }, [loadedLogicContent, state.form]);

  useEffect(() => {
    if (preComputedLogicsRef.current && isMounted.current) {
      applyLogicEngine(
        preComputedLogicsRef.current,
        state.response,
        dispatch,
        initialComponentsRef.current,
      );
    }
  }, [dispatch, state.response]);
};

export { computeLogicItems, generateJsonataExpression, useFormLogic };
